import {type VariantProps} from 'tailwind-variants'
import {type OmitStrict} from 'type-zoo'
import {TableHead} from '~/design-system/foundations'
import {
  type HgTableBodyCellProps,
  HgTableBodyCell,
} from '~/design-system/hg/components'
import {tableCellVariants} from '~/design-system/hg/components/HgTable/variants'
import {dollarsAbbreviator} from '~/utils/dollarsAbbreviator'
import {dollarsPrettifier} from '~/utils/dollarsPrettifier'
import {type PerTransactionFee, type UsageBasedFee} from '../../types'
import {
  getTransactionOverageCost,
  getPerTransactionCost,
  abbreviateAmount,
} from '../../utils'
import BankSwatch, {type BankSwatchProps} from '../BankSwatch'
import {comparisonTableVariants} from './variants'

type ComparisonTableBodyCellProps = OmitStrict<
  HgTableBodyCellProps,
  'alignment' | 'type'
> &
  VariantProps<typeof comparisonTableVariants>

const getPerTransactionCaption = ({fee, transactionType}: PerTransactionFee) => {
  return `${dollarsPrettifier(fee, true, 'twoWhenNecessary')}/${transactionType}`
}

const ComparisonTableTitleCell = ({
  title,
  color,
}: {
  title: string
} & BankSwatchProps) => {
  const {cell, contentWrapper} = tableCellVariants({alignment: 'center'})

  return (
    <TableHead
      scope="col"
      className={cell({className: 'border-dashed border-border-frosted p-s5'})}
    >
      <div className={contentWrapper({className: 'items-center'})}>
        <p className="me-s2 text-text-default arcadia-heading-9">{title}</p>
        <BankSwatch color={color} />
      </div>
    </TableHead>
  )
}

const ComparisonTableBodyCell = ({
  variant,
  type = 'text',
  ...props
}: ComparisonTableBodyCellProps) => {
  const {bodyCellWrapper, bodyCellContent, innerCellTextContent} =
    comparisonTableVariants({variant, type})

  return (
    <HgTableBodyCell
      {...props}
      classNames={{
        cell: bodyCellWrapper(),
        content: bodyCellContent(),
        innerTextContent: innerCellTextContent(),
      }}
      type={type}
      alignment="center"
    />
  )
}

const UsageBasedCell = (props: UsageBasedFee) => {
  const getUsageBasedFeeCaption = ({feeOverLimit, limit}: UsageBasedFee) => {
    const displayLimit =
      dollarsAbbreviator(limit, {
        neverShowCents: true,
        withDollarSign: false,
        withDecimals: 0,
      }) || '0'
    return `${getPerTransactionCaption(feeOverLimit)} past ${displayLimit} limit`
  }

  return (
    <ComparisonTableBodyCell
      body={abbreviateAmount(getTransactionOverageCost(props), {
        threshold: 1_000_000,
        centsThreshold: 1_000_000,
      })}
      caption={getUsageBasedFeeCaption(props)}
    />
  )
}

const PerTransactionCell = (props: PerTransactionFee) => {
  return (
    <ComparisonTableBodyCell
      body={dollarsPrettifier(
        getPerTransactionCost(props),
        true,
        'twoWhenNecessary'
      )}
      caption={getPerTransactionCaption(props)}
    />
  )
}

const TotalCell = ({totalFees}: {totalFees: number}) => {
  return (
    <ComparisonTableBodyCell
      variant="total"
      type="text"
      title={
        <span className="arcadia-heading-5">
          {abbreviateAmount(totalFees, {
            threshold: 1_000_000,
            centsThreshold: 10_000,
          })}
          /mo.
        </span>
      }
    />
  )
}

export {
  ComparisonTableBodyCell,
  ComparisonTableTitleCell,
  UsageBasedCell,
  PerTransactionCell,
  TotalCell,
}
